/**
 * Custom wheel/middle-drag scroll tool that writes the slot's `sliceIndex`
 * atom directly with the next list-index, applying the slot's IPP-derived
 * `listAnatomicSign` so wheel-up (and drag-up) always moves anatomically up
 * (cranial / anterior / patient-right), regardless of how the volume's
 * imageIds happen to be sorted.
 *
 * Atom-first design: the slot's `sliceIndex` is the single source of truth.
 * The tool computes the next list-index and writes the atom; the existing
 * `externalSliceIndex` effect in `VolumeViewport` drives cornerstone (via
 * IPP-based `jumpToWorld` when possible). Skipping cornerstone entirely from
 * the tool means the SSoT can never disagree with the renderer — no
 * K-step↔list-index translation race, no stale-atom-vs-cornerstone drift.
 */

import { getDefaultStore } from "jotai";
import { slotStateFamily } from "../atoms/hangingProtocolAtoms";
import {
  hangingProtocolViewportPrefix,
  SLOT_IDS,
  type SlotId,
} from "../utils/hangingProtocolService";
import { logger } from "@/utils/logger";

interface Viewport {
  id: string;
  element: HTMLElement;
}

function parseSlotFromViewportId(viewportId: string): SlotId | null {
  for (const slot of SLOT_IDS) {
    if (viewportId.startsWith(hangingProtocolViewportPrefix(slot))) return slot;
  }
  return null;
}

/**
 * Step direction (in list-index space) for "anatomic up". +1 means increment
 * list-index moves anatomically up (cranial/anterior/right); -1 means
 * decrement does. Falls back to -1 (PACS default: low list-index = cranial)
 * when the slot's sign is unknown.
 */
function getAnatomicUpStep(slotId: SlotId): 1 | -1 {
  try {
    const sign = getDefaultStore().get(slotStateFamily(slotId)).listAnatomicSign;
    return sign === 1 ? 1 : -1;
  } catch (e) {
    logger.debug("[AnatomicScrollTool] sign lookup failed:", e);
    return -1;
  }
}

export function createAnatomicScrollToolClass(cornerstone: any, cornerstoneTools: any): any {
  const StackScrollTool = cornerstoneTools.StackScrollTool;

  class AnatomicScrollTool extends StackScrollTool {
    static toolName = "AnatomicScroll";

    _scroll(evt: any): void {
      const { wheel, element } = evt.detail;
      const enabledElement = cornerstone.getEnabledElement(element);
      if (!enabledElement) return;
      this._applyStep((enabledElement.viewport as Viewport).id, wheel.direction, 1);
    }

    _scrollDrag(evt: any): void {
      const { deltaPoints, viewportId, renderingEngineId } = evt.detail;
      const enabled = cornerstone.getEnabledElementByIds(viewportId, renderingEngineId);
      if (!enabled) return;
      const viewport = enabled.viewport as Viewport & {
        element: HTMLElement;
        getNumberOfSlices?: () => number;
      };
      const pixelsPerImage = (this as any)._getPixelPerImage(viewport);
      if (!pixelsPerImage) return;
      const deltaY = (deltaPoints.canvas[1] as number) + ((this as any).deltaY ?? 0);
      if (Math.abs(deltaY) < pixelsPerImage) {
        (this as any).deltaY = deltaY;
        return;
      }
      const imageIdIndexOffset = Math.round(deltaY / pixelsPerImage);
      this._applyStep(viewport.id, Math.sign(imageIdIndexOffset), Math.abs(imageIdIndexOffset));
      (this as any).deltaY = deltaY % pixelsPerImage;
    }

    /**
     * Atom write: clamp the requested step to the slot's slice range and set
     * `sliceIndex`. The externalSliceIndex effect in VolumeViewport picks up
     * the change and drives cornerstone via IPP-based jumpToWorld.
     *
     * wheelDirection: -1 = wheel-up (anatomic-up), +1 = wheel-down.
     */
    private _applyStep(viewportId: string, wheelDirection: number, magnitude: number): void {
      const slotId = parseSlotFromViewportId(viewportId);
      if (!slotId) return;
      const store = getDefaultStore();
      const slot = store.get(slotStateFamily(slotId));
      if (slot.totalSlices <= 1) return;
      const upStep = getAnatomicUpStep(slotId);
      const step = -wheelDirection * upStep * magnitude;
      const next = Math.max(0, Math.min(slot.totalSlices - 1, slot.sliceIndex + step));
      if (next === slot.sliceIndex) return;
      store.set(slotStateFamily(slotId), { ...slot, sliceIndex: next });
    }
  }

  return AnatomicScrollTool;
}
